/**
 * Mutations do COFRE DE SENHAS: CRUD de cofres/pastas/entradas e o REVEAL —
 * único ponto onde o segredo cruza a rede (auditado no servidor). Reveal é
 * mutation de propósito: o segredo nunca entra no cache de queries.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { Vault, VaultBody, VaultEntry, VaultEntryBody, VaultFolder } from '@erp/shared';

const VAULT_KEY = ['erp', 'vault'] as const;

// ── Cofres ──────────────────────────────────────────────────────────────────

export function useCreateVault() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: VaultBody) => {
      const res = await api<Vault>('/vault', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: VAULT_KEY }),
  });
}

export function useUpdateVault() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ vaultId, body }: { vaultId: number; body: VaultBody }) => {
      const res = await api<Vault>(`/vault/${vaultId}`, { method: 'PUT', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: VAULT_KEY }),
  });
}

export function useDeleteVault() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vaultId: number) => {
      const res = await api<void>(`/vault/${vaultId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: VAULT_KEY }),
  });
}

// ── Pastas ──────────────────────────────────────────────────────────────────

export interface VaultFolderBody {
  name: string;
  parentId?: number | null;
}

export function useCreateVaultFolder(vaultId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: VaultFolderBody) => {
      const res = await api<VaultFolder>(`/vault/${vaultId}/folders`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: VAULT_KEY }),
  });
}

export function useUpdateVaultFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ folderId, body }: { folderId: number; body: VaultFolderBody }) => {
      const res = await api<VaultFolder>(`/vault/folders/${folderId}`, { method: 'PUT', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: VAULT_KEY }),
  });
}

export function useDeleteVaultFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (folderId: number) => {
      const res = await api<void>(`/vault/folders/${folderId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: VAULT_KEY }),
  });
}

// ── Entradas (senhas) ───────────────────────────────────────────────────────

export function useCreateVaultEntry(vaultId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: VaultEntryBody) => {
      const res = await api<VaultEntry>(`/vault/${vaultId}/entries`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: VAULT_KEY }),
  });
}

export function useUpdateVaultEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ entryId, body }: { entryId: number; body: VaultEntryBody }) => {
      const res = await api<VaultEntry>(`/vault/entries/${entryId}`, { method: 'PUT', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: VAULT_KEY }),
  });
}

export function useDeleteVaultEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: number) => {
      const res = await api<void>(`/vault/entries/${entryId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: VAULT_KEY }),
  });
}

// ── Reveal ──────────────────────────────────────────────────────────────────

/**
 * POST /vault/entries/:id/reveal → segredo em texto claro. MUTATION (nunca
 * query): o valor não entra no cache; quem chama decide o destino (estado
 * local de um dialog aberto ou clipboard) e deve descartá-lo no fechamento.
 * Cada reveal é auditado no servidor — refrescamos só os logs, não a árvore.
 */
export function useRevealVaultEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: number) => {
      const res = await api<{ secret: string }>(`/vault/entries/${entryId}/reveal`, {
        method: 'POST',
      });
      if (res.error) throw res.error;
      return res.data.secret;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['erp', 'vault', 'logs'] }),
  });
}
